import { useCallback, useEffect, useMemo, useState } from 'react';
import resolveApiBase from './apiBase';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;

const resolveUrl = (path) => {
    if (!API_BASE) {
        return path;
    }
    const normalisedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${normalisedBase}${path.startsWith('/') ? path : `/${path}`}`;
};

const fetchJson = async (path, signal) => {
    const response = await fetch(resolveUrl(path), {
        headers: {
            Accept: 'application/json',
            ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
        },
        signal,
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Request failed with status ${response.status}`);
    }

    return response.json();
};

const normaliseKey = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim().toLowerCase();
};

export const useTelemetryCatalog = () => {
    const [state, setState] = useState({
        status: 'idle',
        accounts: [],
        strategies: [],
        accountStrategies: [],
        error: null,
    });

    const load = useCallback(async (signal) => {
        setState((prev) => ({ ...prev, status: 'loading', error: null }));
        try {
            const [accounts, strategies, accountStrategies] = await Promise.all([
                fetchJson('/api/catalog/accounts', signal),
                fetchJson('/api/catalog/strategies', signal),
                fetchJson('/api/catalog/account-strategies', signal),
            ]);

            setState({
                status: 'ready',
                accounts: Array.isArray(accounts) ? accounts : [],
                strategies: Array.isArray(strategies) ? strategies : [],
                accountStrategies: Array.isArray(accountStrategies)
                    ? accountStrategies
                    : [],
                error: null,
            });
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }
            setState({
                status: 'error',
                accounts: [],
                strategies: [],
                accountStrategies: [],
                error,
            });
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    const refresh = useCallback(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    const lookups = useMemo(() => {
        const accountLookup = new Map();
        const strategyLookup = new Map();
        const accountsToStrategies = new Map();
        const strategiesToAccounts = new Map();

        state.accounts.forEach((account) => {
            const key = normaliseKey(account.accountId || account.account_id);
            if (key) {
                accountLookup.set(key, account);
            }
        });

        state.strategies.forEach((strategy) => {
            const key = normaliseKey(strategy.strategyId || strategy.strategy_id || strategy.strategy_name);
            if (key) {
                strategyLookup.set(key, strategy);
            }
        });

        state.accountStrategies.forEach((entry) => {
            const accountId = normaliseKey(entry.accountId || entry.client_name || entry.clientName || entry.app_name);
            const strategyId = normaliseKey(entry.strategyId || entry.strategy_name || entry.strategyName);
            if (!accountId || !strategyId) {
                return;
            }

            if (!accountsToStrategies.has(accountId)) {
                accountsToStrategies.set(accountId, []);
            }
            if (!strategiesToAccounts.has(strategyId)) {
                strategiesToAccounts.set(strategyId, []);
            }
            accountsToStrategies.get(accountId).push(entry);
            strategiesToAccounts.get(strategyId).push(entry);
        });

        return {
            accountLookup,
            strategyLookup,
            accountsToStrategies,
            strategiesToAccounts,
        };
    }, [state.accounts, state.accountStrategies, state.strategies]);

    return {
        ...state,
        refresh,
        lookups,
    };
};
